import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import '../../node_modules/lit-html/lit-html.js';
import { LitElement as s } from '../../node_modules/lit-element/lit-element.js';
import { version } from '../../package.json.js';

class PvdBaseComponent extends s {
    static get namespace() {
        return this._namespace || 'pvd';
    }
    static set namespace(value) {
        this._namespace = value;
    }
    static get customElementName() {
        return this._customElementName;
    }
    static set customElementName(value) {
        this._customElementName = value;
    }
    /**
     * Define Providence components within the CustomElementRegistry.
     * @param namespace a string used to namespace components in order to avoid conflicting with other Providence versions
     * @see {@link https://providence.fmr.com/4.x/getting-started-for-developers/developer-overview.html|Getting started for developers}
     */
    static defineCustomElement(namespace = 'pvd', customElementClass = this) {
        // Warn that the required 'componentName' property is missing so that it is
        // easier to identify these issues during development
        if (!customElementClass.componentName) {
            console.warn("Missing required static 'componentName' property");
        }
        const customElementName = `${namespace}-${customElementClass.componentName}`;
        this.namespace = namespace;
        this.customElementName = customElementName; // Save the customElement name so it can be referenced later
        if (!PvdBaseComponent.customElementDefined(customElementName)) {
            customElements.define(customElementName, customElementClass);
        }
    }
    static customElementDefined(customElementName) {
        return customElements.get(customElementName) !== undefined;
    }
    static generateRandomNumber(max = 1000000000000) {
        return Math.floor(Math.random() * Math.floor(max));
    }
    /**
     * Determine if a provided attribute has changed and validate the new value
     * if it has changed
     * @param validAttributes
     */
    static hasChanged(validAttributes) {
        const values = (obj) => {
            const arr = [];
            for (const idx in obj) {
                arr.push(obj[idx]);
            }
            return arr;
        };
        const validAttributesArray = !Array.isArray(validAttributes)
            ? values(validAttributes)
            : validAttributes;
        return {
            hasChanged: (val, oldVal) => {
                const isValid = (valid) => val === valid;
                if (val === oldVal) {
                    return false;
                }
                if (!validAttributesArray.some(isValid)) {
                    console.warn(`'${val}' is an invalid attribute value (expected one of: ${validAttributesArray.join(', ')})`);
                }
                return true;
            },
        };
    }
    /**
     * Define conversions for going from an attribute to a property and vice versa.
     * @param attributeName name of the boolean attribute
     */
    static booleanAttribute(attributeName) {
        return {
            converter: {
                fromAttribute: (val) => {
                    const attrVal = val ? val.trim() : val;
                    return attrVal === '' || attrVal === 'true' || attrVal === attributeName;
                },
                toAttribute: (val) => {
                    return val;
                },
            },
        };
    }
    /**
     * Handles the conversion for the case where an attribute's presence is viewed to determine
     * whether or not it has an effect. If the attribute is present but does not contain a value,
     * the converter uses the default value that is passed in. The converter also ensures that the
     * default value is not written back to the attribute in the DOM and stays for use internally.
     * @param defaultValue - string containing the default value to use when the attribute is present but not customized
     */
    static configurablePresenceAttribute(defaultValue) {
        return {
            converter: {
                fromAttribute(value) {
                    return value === '' ? defaultValue : value;
                },
                // when using the default, we want don't want to put that in the attribute in the DOM
                toAttribute(value) {
                    return value === defaultValue ? '' : value;
                },
            },
        };
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this.dispatchEvent(new CustomEvent('pvd-component-ready', {
            bubbles: true,
            detail: {
                component: PvdBaseComponent.customElementName,
            },
        }));
        // check if className was added by a framework and set it up if so
        if (this.getAttribute('className')) {
            this.className = `${this.getAttribute('className')} ${Array.from(this.classList).join(' ')}`;
        }
        /**
         * Frameworks like Vue and React add a className attribute to HTML elements that they know about
         * that maps properly to the property of the same name. With custom elements, they're not able to
         * do this reach in to the component so we enable that functionality using a mutation observer.
         */
        const classNameMutationCallback = (mutationsList) => {
            mutationsList.forEach((mutation) => {
                switch (mutation.attributeName) {
                    case 'class':
                        const classList = Array.from(this.classList).join(' ');
                        if (this.className !== classList) {
                            this.className = classList;
                        }
                        break;
                    case 'classname':
                    case 'className':
                        const oldClasses = mutation.oldValue.split(' ');
                        const newClasses = this.getAttribute('className').split(' ');
                        if (oldClasses !== newClasses) {
                            this.classList.remove(...oldClasses.filter(oldClass => !newClasses.includes(oldClass)));
                            this.classList.add(...newClasses);
                        }
                        break;
                }
            });
        };
        this.classNameObserver = new MutationObserver(classNameMutationCallback);
        this.classNameObserver.observe(this, {
            attributes: true,
            attributeFilter: ['class', 'className', 'classname'],
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        var _a;
        super.disconnectedCallback();
        (_a = this.classNameObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        this.dispatchEvent(new CustomEvent('pvd-component-updated', {
            bubbles: true,
            detail: {
                component: PvdBaseComponent.customElementName,
            },
        }));
    }
    // to be overwritten by the subclass
    get classMap() {
        return {};
    }
    createClassName(classMap = this.classMap) {
        let classList = '';
        for (const prop in classMap) {
            let modifierPrefix = classMap.modifierPrefix || false;
            if (typeof classMap[prop] === 'object') {
                const className = classMap[prop].className;
                if (classMap[prop].modifierPrefix !== undefined) {
                    modifierPrefix = classMap[prop].modifierPrefix;
                }
                if (classMap[prop].condition) {
                    if (typeof className === 'object') {
                        for (const singleClass in className) {
                            classList = this.addClass(classList, className[singleClass], modifierPrefix);
                        }
                    }
                    else {
                        classList = this.addClass(classList, className, modifierPrefix);
                    }
                }
            }
            else {
                const className = classMap[prop];
                switch (prop) {
                    case 'default':
                        classList = this.addClass(classList, className);
                        break;
                    default:
                        if (this[prop]) {
                            classList = this.addClass(classList, className, modifierPrefix);
                        }
                        break;
                }
            }
        }
        return classList;
    }
    addClass(classList, newClass, modifierPrefix = false) {
        newClass = modifierPrefix ? `${modifierPrefix}--${newClass}` : newClass;
        return classList.length === 0 ? newClass : `${classList} ${newClass}`;
    }
    /**
     * Returns where to render the component's template.
     * It is configured by the shadowDOM property on the component definition.
     */
    createRenderRoot() {
        if (this.constructor.shadowDOM) {
            return super.createRenderRoot();
        }
        return this;
    }
    /**
     * Clone and dispatch and event that doesn't bubble by default
     * @param e event where `event.bubbles` === `false`
     */
    bubbleEvent(e) {
        // grabbing the HTML element of 'this' instance
        this.dispatchEvent(new Event(e.type, e));
    }
    /**
     * For a11y attributes, if the value is set to 'false' the attribute should be completely removed off the DOM, not aria-*="false"
     * This function, with the help of ifDefined(), will return undefined to remove a11y props with a `false` value off the DOM
     * @param val property that you are validating where if its false, gets set to undefined
     */
    a11yBoolean(val) {
        return val ? true : undefined;
    }
    /**
     * For custom attributes, this function allows custom attributes to be set/removed on specific elements
     * @param newAttr the object of attributes that are needing to be added
     * @param prevAttr optional, if there were custom attributes added prior, this is used to determine if attributes need to be removed/updated
     * @param propertyMap a mapping of 'protected' attributes that tie into properties of a element in a web component
     * @param targetElement the element that the attributes should be added/removed from
     */
    setNativeAttributes(newAttr, propertyMap, targetElement, prevAttr) {
        if (newAttr && targetElement) {
            if (prevAttr && propertyMap) {
                // removes attributes that are not being applied via props
                this.removePreviousAttributes(prevAttr, propertyMap, targetElement);
            }
            Object.keys(newAttr).map((attribute) => {
                // if the attribute is in the 'protected' attribute map, throw warning and do not add
                if (propertyMap.get(attribute)) {
                    console.warn(`use '${propertyMap.get(attribute)}' to set the '${attribute}' attribute`);
                }
                else {
                    // double == here to be inclusive of `null` and `undefined`
                    if (typeof newAttr[attribute] === 'boolean' || newAttr[attribute] == undefined) {
                        // if the attribute is a boolean, and is true, add attribute using empty strings as value
                        // otherwise, remove attribute
                        newAttr[attribute] === true
                            ? targetElement.setAttribute(attribute, '')
                            : targetElement.removeAttribute(attribute);
                    }
                    else {
                        // adds new attribute to the target element
                        targetElement.setAttribute(attribute, newAttr[attribute].toString());
                    }
                }
            });
        }
    }
    /**
     * Logic used to remove attributes that are not being placed on an element via props
     * @param prevAttr if there were custom attributes added prior, this is used to determine if attributes need to be removed/updated
     * @param propertyMap a mapping of 'protected' attributes that tie into properties of a element in a web component
     * @param targetElement the element that the attributes should be removed from
     */
    removePreviousAttributes(prevAttr, propertyMap, targetElement) {
        Object.keys(prevAttr).map((attribute) => {
            // if the attribute is not in the 'protected' attribute map, remove
            if (!propertyMap.get(attribute)) {
                targetElement.removeAttribute(attribute);
            }
        });
    }
}
PvdBaseComponent.shadowDOM = false;
PvdBaseComponent.version = version;

export { PvdBaseComponent };
//# sourceMappingURL=pvd-base-component.js.map
